const ALLOWED_VIDEO_HOSTS = [
    'youtube.com',
    'youtu.be',
    'vimeo.com',
    'tiktok.com',
    'instagram.com',
    'facebook.com',
    'fb.watch',
    'dailymotion.com',
    'dai.ly',
    'wistia.com',
    'fast.wistia.net',
    'loom.com',
    'drive.google.com',
];

const matchesAllowedHost = (hostname) => {
    const normalized = hostname.toLowerCase();

    return ALLOWED_VIDEO_HOSTS.some(
        (host) => normalized === host || normalized.endsWith(`.${host}`)
    );
};

export const isGoogleDriveVideoHost = (hostname) => {
    const normalized = String(hostname || '').toLowerCase();

    return normalized === 'drive.google.com' || normalized.endsWith('.drive.google.com');
};

const isYouTubeHost = (hostname) => {
    const normalized = String(hostname || '').toLowerCase();

    return (
        normalized === 'youtu.be' ||
        normalized === 'youtube.com' ||
        normalized.endsWith('.youtube.com')
    );
};

const isVimeoHost = (hostname) => {
    const normalized = String(hostname || '').toLowerCase();

    return normalized === 'vimeo.com' || normalized.endsWith('.vimeo.com');
};

const isInstagramHost = (hostname) => {
    const normalized = String(hostname || '').toLowerCase();

    return normalized === 'instagram.com' || normalized.endsWith('.instagram.com');
};

const INSTAGRAM_MEDIA_PATHS = new Set(['reel', 'p', 'tv']);

/**
 * @param {URL | string} value
 * @returns {{ mediaType: string, id: string } | null}
 */
export const extractInstagramMedia = (value) => {
    try {
        const url = value instanceof URL ? value : new URL(String(value).trim());

        if (!isInstagramHost(url.hostname)) {
            return null;
        }

        const parts = url.pathname.split('/').filter(Boolean);

        if (parts.length < 2 || !INSTAGRAM_MEDIA_PATHS.has(parts[0])) {
            return null;
        }

        const mediaType = parts[0];
        const id = parts[1];

        if (!id || id === 'embed') {
            return null;
        }

        return { mediaType, id };
    } catch {
        return null;
    }
};

/**
 * @param {URL | string} value
 * @returns {string | null}
 */
export const getInstagramEmbedUrl = (value) => {
    const media = extractInstagramMedia(value);

    if (!media) {
        return null;
    }

    return `https://www.instagram.com/${media.mediaType}/${media.id}/embed`;
};

/**
 * @param {URL | string} value
 * @returns {string | null}
 */
export const getInstagramCanonicalUrl = (value) => {
    const media = extractInstagramMedia(value);

    if (!media) {
        return null;
    }

    return `https://www.instagram.com/${media.mediaType}/${media.id}/`;
};

const isTikTokHost = (hostname) => {
    const normalized = String(hostname || '').toLowerCase();

    return normalized === 'tiktok.com' || normalized.endsWith('.tiktok.com');
};

const TIKTOK_SHORT_LINK_HOSTS = new Set(['vm.tiktok.com', 'm.tiktok.com']);

/**
 * @param {URL | string} value
 * @returns {string | null}
 */
export const extractTikTokVideoId = (value) => {
    try {
        const url = value instanceof URL ? value : new URL(String(value).trim());

        if (!isTikTokHost(url.hostname)) {
            return null;
        }

        const parts = url.pathname.split('/').filter(Boolean);
        const videoIndex = parts.indexOf('video');

        if (videoIndex >= 0 && parts[videoIndex + 1] && /^\d+$/.test(parts[videoIndex + 1])) {
            return parts[videoIndex + 1];
        }

        if (parts[0] === 'embed' && parts[1] === 'v2' && parts[2] && /^\d+$/.test(parts[2])) {
            return parts[2];
        }

        return null;
    } catch {
        return null;
    }
};

/**
 * @param {URL | string} value
 * @returns {string | null}
 */
export const getTikTokEmbedUrl = (value) => {
    const videoId = extractTikTokVideoId(value);

    if (!videoId) {
        return null;
    }

    return `https://www.tiktok.com/embed/v2/${videoId}`;
};

/**
 * @param {URL | string} value
 * @returns {string | null}
 */
export const getTikTokCanonicalUrl = (value) => {
    try {
        const url = value instanceof URL ? value : new URL(String(value).trim());
        const videoId = extractTikTokVideoId(url);

        if (!videoId) {
            return null;
        }

        const parts = url.pathname.split('/').filter(Boolean);
        const videoIndex = parts.indexOf('video');

        if (videoIndex > 0) {
            return `https://www.tiktok.com/${parts.slice(0, videoIndex + 2).join('/')}/`;
        }

        return `https://www.tiktok.com/video/${videoId}`;
    } catch {
        return null;
    }
};

/**
 * @param {URL} url
 * @returns {string | null}
 */
export const extractGoogleDriveFileId = (url) => {
    const pathMatch = url.pathname.match(/\/file\/d\/([^/]+)/);
    if (pathMatch?.[1]) {
        return pathMatch[1];
    }

    const idParam = url.searchParams.get('id');
    if (idParam) {
        return idParam;
    }

    return null;
};

/**
 * @param {URL | string} value
 * @returns {string | null}
 */
export const extractYouTubeId = (value) => {
    try {
        const url = value instanceof URL ? value : new URL(String(value).trim());
        const host = url.hostname.replace(/^www\./, '').toLowerCase();

        if (host === 'youtu.be') {
            return url.pathname.slice(1).split('/')[0] || null;
        }

        if (host.endsWith('youtube.com')) {
            const fromQuery = url.searchParams.get('v');
            if (fromQuery) {
                return fromQuery;
            }

            const parts = url.pathname.split('/').filter(Boolean);
            if (parts.length >= 2 && ['embed', 'shorts', 'v'].includes(parts[0])) {
                return parts[1];
            }
        }
    } catch {
        return null;
    }

    return null;
};

/**
 * @param {URL | string} value
 * @returns {string | null}
 */
export const extractVimeoId = (value) => {
    try {
        const url = value instanceof URL ? value : new URL(String(value).trim());
        const host = url.hostname.replace(/^www\./, '').toLowerCase();

        if (!host.endsWith('vimeo.com')) {
            return null;
        }

        const parts = url.pathname.split('/').filter(Boolean);
        const videoIndex = parts.indexOf('video');

        if (videoIndex >= 0 && parts[videoIndex + 1]) {
            return parts[videoIndex + 1];
        }

        return parts[parts.length - 1] || null;
    } catch {
        return null;
    }
};

/**
 * @param {string | URL} value
 * @returns {string | null}
 */
export const getGoogleDriveEmbedUrl = (value) => {
    try {
        const url = value instanceof URL ? value : new URL(String(value).trim());
        const fileId = extractGoogleDriveFileId(url);

        if (!fileId) {
            return null;
        }

        return `https://drive.google.com/file/d/${fileId}/preview`;
    } catch {
        return null;
    }
};

/**
 * @param {string | null | undefined} videoProvider
 * @param {string | null | undefined} videoId
 * @returns {string | null}
 */
export const getProductVideoThumbnail = (videoProvider, videoId) => {
    if (!videoId) {
        return null;
    }

    const provider = String(videoProvider || '').toLowerCase();

    if (provider === 'youtube') {
        return `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;
    }

    if (provider === 'vimeo') {
        return `https://vumbnail.com/${videoId}.jpg`;
    }

    if (provider === 'google_drive' || provider === 'drive') {
        return `https://drive.google.com/thumbnail?id=${videoId}&sz=w640`;
    }

    return null;
};

/**
 * @param {string} videoUrl
 * @param {string | null | undefined} videoProvider
 * @param {string | null | undefined} videoId
 * @returns {{ thumbnailSrc: string | null, embedSrc: string | null, provider: string | null, videoId: string | null }}
 */
export const resolveProductVideoPreview = ({
    videoUrl,
    videoProvider = null,
    videoId = null,
}) => {
    const trimmed = String(videoUrl || '').trim();

    if (!trimmed) {
        return { thumbnailSrc: null, embedSrc: null, provider: null, videoId: null };
    }

    let url;
    try {
        url = new URL(trimmed);
    } catch {
        return { thumbnailSrc: null, embedSrc: null, provider: null, videoId: null };
    }

    let provider = String(videoProvider || '').toLowerCase() || null;
    let id = videoId || null;

    if (!provider) {
        if (isYouTubeHost(url.hostname)) {
            provider = 'youtube';
        } else if (isVimeoHost(url.hostname)) {
            provider = 'vimeo';
        } else if (isGoogleDriveVideoHost(url.hostname)) {
            provider = 'google_drive';
        } else if (isInstagramHost(url.hostname)) {
            provider = 'instagram';
        } else if (isTikTokHost(url.hostname)) {
            provider = 'tiktok';
        }
    }

    if (!id) {
        if (provider === 'youtube' || isYouTubeHost(url.hostname)) {
            id = extractYouTubeId(url);
            provider = 'youtube';
        } else if (provider === 'vimeo' || isVimeoHost(url.hostname)) {
            id = extractVimeoId(url);
            provider = 'vimeo';
        } else if (provider === 'google_drive' || provider === 'drive' || isGoogleDriveVideoHost(url.hostname)) {
            id = extractGoogleDriveFileId(url);
            provider = 'google_drive';
        } else if (provider === 'instagram' || isInstagramHost(url.hostname)) {
            const media = extractInstagramMedia(url);
            if (media) {
                id = media.id;
                provider = 'instagram';
            }
        } else if (provider === 'tiktok' || isTikTokHost(url.hostname)) {
            const tiktokId = extractTikTokVideoId(url);
            if (tiktokId) {
                id = tiktokId;
                provider = 'tiktok';
            }
        }
    }

    let embedSrc = null;

    if (provider === 'youtube' && id) {
        embedSrc = `https://www.youtube.com/embed/${id}`;
    } else if (provider === 'vimeo' && id) {
        embedSrc = `https://player.vimeo.com/video/${id}`;
    } else if ((provider === 'google_drive' || provider === 'drive') && id) {
        embedSrc = `https://drive.google.com/file/d/${id}/preview`;
    } else if (provider === 'instagram' || isInstagramHost(url.hostname)) {
        embedSrc = getInstagramEmbedUrl(url);
        if (embedSrc) {
            provider = 'instagram';
        }
    } else if (provider === 'tiktok' || isTikTokHost(url.hostname)) {
        embedSrc = getTikTokEmbedUrl(url);
        if (embedSrc) {
            provider = 'tiktok';
        }
    }

    const thumbnailSrc = getProductVideoThumbnail(provider, id);

    return {
        thumbnailSrc,
        embedSrc,
        provider,
        videoId: id,
    };
};

/**
 * @param {string} value
 * @returns {string}
 */
export const normalizeProductVideoUrlForStorage = (value) => {
    if (!value || !String(value).trim()) {
        return '';
    }

    const trimmed = String(value).trim();

    try {
        const url = new URL(trimmed);

        if (isGoogleDriveVideoHost(url.hostname)) {
            return getGoogleDriveEmbedUrl(url) || trimmed;
        }

        if (isInstagramHost(url.hostname)) {
            return getInstagramCanonicalUrl(url) || trimmed;
        }

        if (isTikTokHost(url.hostname)) {
            return getTikTokCanonicalUrl(url) || trimmed;
        }
    } catch {
        return trimmed;
    }

    return trimmed;
};

/**
 * @param {string} value
 * @returns {true | 'empty' | 'invalid' | 'https' | 'provider'}
 */
export const validateProductVideoUrl = (value) => {
    if (!value || !String(value).trim()) {
        return true;
    }

    const trimmed = String(value).trim();
    let url;

    try {
        url = new URL(trimmed);
    } catch {
        return 'invalid';
    }

    if (url.protocol !== 'https:') {
        return 'https';
    }

    if (!matchesAllowedHost(url.hostname)) {
        return 'provider';
    }

    if (isGoogleDriveVideoHost(url.hostname) && !extractGoogleDriveFileId(url)) {
        return 'invalid';
    }

    if (isInstagramHost(url.hostname) && !extractInstagramMedia(url)) {
        return 'invalid';
    }

    if (isTikTokHost(url.hostname) && !extractTikTokVideoId(url)) {
        const host = url.hostname.toLowerCase();
        if (!TIKTOK_SHORT_LINK_HOSTS.has(host)) {
            return 'invalid';
        }
    }

    return true;
};
